import { AgChartOptions } from "ag-charts-community";

import {
  AXIS_LABEL_COLOR,
  AXIS_LINE_COLOR,
  CHART_AXIS_TITLE_FONT_SIZE,
  GRID_LINE_COLOR,
  HEATMAP_CELL_STROKE_COLOR,
  HEATMAP_CELL_STROKE_WIDTH,
  HEATMAP_GRADIENT_LENGTH,
  HEATMAP_GRADIENT_THICKNESS,
  analyticsChartColors,
} from "../constants";
import { formatAnalyticsNumber } from "../utils/formatAnalyticsNumber";

import { baseNonCartesianAnalyticsChartOptions } from "./base";
import { createAnalyticsTooltipResult, formatAxisValue } from "./customChartFormatters";

import type { CustomChartResponseItem } from "@/queries/analytics-custom-charts-types";

const HEATMAP_GRADIENT_LEGEND_INTERVALS = 4;

interface HeatmapDatum {
  x: string;
  y: string;
  value: number;
}

export const buildHeatmapCustomChartOptions = (chart: CustomChartResponseItem): AgChartOptions => {
  const unit = chart.series[0]?.unit ?? "number";
  const valueLabel = chart.series[0]?.label ?? "Value";
  const formatValue = (value: number) => formatAxisValue(unit, value);
  const data: HeatmapDatum[] = chart.data.map(row => ({
    x: String(row.x ?? row.label),
    y: String(row.y ?? ""),
    value: Number(row.value ?? 0),
  }));
  const maxValue = Math.max(...data.map(entry => entry.value), 0);
  const gradientLegendIntervalStep =
    maxValue > 0 ? maxValue / HEATMAP_GRADIENT_LEGEND_INTERVALS : undefined;

  return {
    ...baseNonCartesianAnalyticsChartOptions,
    data,
    axes: [
      {
        type: "category",
        position: "bottom",
        label: {
          color: AXIS_LABEL_COLOR,
          fontSize: CHART_AXIS_TITLE_FONT_SIZE,
        },
        line: {
          stroke: AXIS_LINE_COLOR,
        },
        gridLine: {
          style: [{ stroke: GRID_LINE_COLOR }],
        },
      },
      {
        type: "category",
        position: "left",
        label: {
          color: AXIS_LABEL_COLOR,
          fontSize: CHART_AXIS_TITLE_FONT_SIZE,
        },
        line: {
          stroke: AXIS_LINE_COLOR,
        },
        gridLine: {
          style: [{ stroke: GRID_LINE_COLOR }],
        },
      },
    ],
    series: [
      {
        type: "heatmap",
        xKey: "x",
        yKey: "y",
        colorKey: "value",
        colorName: valueLabel,
        colorRange: analyticsChartColors.requestActivityHeatmap,
        stroke: HEATMAP_CELL_STROKE_COLOR,
        strokeWidth: HEATMAP_CELL_STROKE_WIDTH,
        label: {
          enabled: false,
        },
        tooltip: {
          renderer: ({ datum }: { datum: HeatmapDatum }) =>
            createAnalyticsTooltipResult({
              title: datum.y,
              rows: [
                { label: "Category", value: datum.x, color: AXIS_LABEL_COLOR },
                { label: valueLabel, value: formatValue(datum.value), color: AXIS_LABEL_COLOR },
              ],
            }),
        },
      },
    ],
    gradientLegend: {
      enabled: true,
      position: "bottom",
      gradient: {
        thickness: HEATMAP_GRADIENT_THICKNESS,
        preferredLength: HEATMAP_GRADIENT_LENGTH,
      },
      scale: {
        interval: gradientLegendIntervalStep ? { step: gradientLegendIntervalStep } : undefined,
        label: {
          color: AXIS_LABEL_COLOR,
          fontSize: CHART_AXIS_TITLE_FONT_SIZE,
          formatter: ({ value }: { value: number | string }) =>
            formatAnalyticsNumber(Number(value)),
        },
      },
    },
    legend: {
      enabled: false,
    },
  } as AgChartOptions;
};
